import React, { useState } from 'react';
import { AppShell, useMantineTheme } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { VideoGateContext } from './facilitator/VideoGateContext';
import { useRouteMatch, useHistory } from 'react-router-dom';
import {
  FACILITATOR_CERTIFICATE_ROUTE,
  flatFacilitatorRoutes,
} from './facilitator/flow';
import SideNav from './facilitator/SideNav';
import Footer from './facilitator/Footer';
import Routes from './facilitator/Routes';
import FacilitatorHeader from './facilitator/Header';
import SubrouteProgress from './facilitator/SubrouteProgress';
import {
  FACILITATOR_MOBILE_NAV_MAX_WIDTH,
  FACILITATOR_NAV_BREAKPOINT,
} from './facilitator/navConfig';
import { isFacilitatorTrainingUnlockedForTesting } from '../useFacilitatorStore';
import {
  ADMIN_PROGRESS_QUERY_KEY,
  deriveProgressFromPath,
  deriveSequentialNextProgress,
  deriveLastVisitedRouteFromProgress,
  hasStartedProgramFromProgress,
  hasProgressPassedPath,
  isPathAccessibleForProgress,
  useAdminProgressMutation,
  useAdminProgressQuery,
} from '../useAdminProgressQuery';
import { hasFullFacilitatorProgramAccessRole } from '../../../utils/authRole';

const FACILITATOR_HOME_ROUTE = '/facilitator/home';
const FACILITATOR_RESOURCE_LIBRARY_ROUTE = '/facilitator/resource-library';

const createRouteGate = (path = '') => ({
  path,
  requiresVideoCompletion: false,
  hasCompletedVideo: false,
  requiresQuizPass: false,
  hasPassedQuiz: false,
  requiresActivityCompletion: false,
  hasCompletedActivity: false,
});

const FascilitatorTraining = () => {
  const theme = useMantineTheme();
  const queryClient = useQueryClient();
  const isMobileNav = useMediaQuery(
    `(max-width: ${FACILITATOR_MOBILE_NAV_MAX_WIDTH}px)`,
  );
  const { path } = useRouteMatch();
  const history = useHistory();
  const [opened, setOpened] = useState(false);
  const [routeGate, setRouteGate] = useState(createRouteGate);
  const [hasSettledCurrentRouteGate, setHasSettledCurrentRouteGate] =
    useState(false);
  const saveAdminProgressMutation = useAdminProgressMutation();
  const currentPath = history.location.pathname;
  const hasFullFacilitatorProgramAccess =
    hasFullFacilitatorProgramAccessRole(localStorage.getItem('role'));
  const trainingRoutes = React.useMemo(
    () =>
      flatFacilitatorRoutes.filter(
        (route) =>
          !hasFullFacilitatorProgramAccess ||
          route.fullUrl !== FACILITATOR_CERTIFICATE_ROUTE,
      ),
    [hasFullFacilitatorProgramAccess],
  );
  const isCurrentPathInFlow = React.useMemo(
    () =>
      trainingRoutes.some((route) => route.fullUrl === currentPath),
    [currentPath, trainingRoutes],
  );

  const {
    adminProgress,
    hasLoadedAdminProgress,
    isLoadingAdminProgress,
  } = useAdminProgressQuery({
    currentPath: isCurrentPathInFlow ? currentPath : '',
  });
  const hasCompletedTrainingGate =
    hasFullFacilitatorProgramAccess ||
    adminProgress.preSurveyCompleted ||
    hasStartedProgramFromProgress(adminProgress);
  const isTrainingUnlocked = isFacilitatorTrainingUnlockedForTesting
    ? true
    : hasCompletedTrainingGate;

  React.useEffect(() => {
    if (
      !hasLoadedAdminProgress &&
      !isFacilitatorTrainingUnlockedForTesting &&
      !hasFullFacilitatorProgramAccess
    ) {
      return;
    }

    if (!isTrainingUnlocked && isCurrentPathInFlow) {
      history.replace(FACILITATOR_HOME_ROUTE);
    }
  }, [
    currentPath,
    hasFullFacilitatorProgramAccess,
    hasLoadedAdminProgress,
    history,
    isCurrentPathInFlow,
    isTrainingUnlocked,
  ]);

  React.useEffect(() => {
    if (
      hasFullFacilitatorProgramAccess ||
      !isTrainingUnlocked ||
      !isCurrentPathInFlow ||
      !hasLoadedAdminProgress
    ) {
      return;
    }

    if (isPathAccessibleForProgress(currentPath, adminProgress)) {
      return;
    }

    const fallbackRoute =
      deriveLastVisitedRouteFromProgress(adminProgress) ||
      FACILITATOR_HOME_ROUTE;

    if (fallbackRoute !== currentPath) {
      history.replace(fallbackRoute);
    }
  }, [
    adminProgress,
    currentPath,
    hasFullFacilitatorProgramAccess,
    hasLoadedAdminProgress,
    history,
    isCurrentPathInFlow,
    isTrainingUnlocked,
  ]);

  React.useEffect(() => {
    if (!isMobileNav && opened) {
      setOpened(false);
    }
  }, [isMobileNav, opened]);

  React.useEffect(() => {
    if (!isCurrentPathInFlow) {
      setHasSettledCurrentRouteGate(true);
      return;
    }

    setHasSettledCurrentRouteGate(false);

    const frameId = window.requestAnimationFrame(() => {
      setHasSettledCurrentRouteGate(true);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
    };
  }, [currentPath, isCurrentPathInFlow]);

  const videoGateValue = React.useMemo(
    () => ({
      registerVideoRequirement: () => {
        setRouteGate((currentGate) => ({
          ...(currentGate.path === currentPath
            ? currentGate
            : createRouteGate(currentPath)),
          requiresVideoCompletion: true,
          hasCompletedVideo: false,
        }));
      },
      markVideoCompleted: () => {
        setRouteGate((currentGate) => ({
          ...(currentGate.path === currentPath
            ? currentGate
            : createRouteGate(currentPath)),
          requiresVideoCompletion: true,
          hasCompletedVideo: true,
        }));
      },
      registerQuizRequirement: () => {
        setRouteGate((currentGate) => ({
          ...(currentGate.path === currentPath
            ? currentGate
            : createRouteGate(currentPath)),
          requiresQuizPass: true,
          hasPassedQuiz: false,
        }));
      },
      markQuizPassed: () => {
        setRouteGate((currentGate) => ({
          ...(currentGate.path === currentPath
            ? currentGate
            : createRouteGate(currentPath)),
          requiresQuizPass: true,
          hasPassedQuiz: true,
        }));
      },
      registerActivityRequirement: () => {
        setRouteGate((currentGate) => ({
          ...(currentGate.path === currentPath
            ? currentGate
            : createRouteGate(currentPath)),
          requiresActivityCompletion: true,
          hasCompletedActivity: false,
        }));
      },
      markActivityCompleted: () => {
        setRouteGate((currentGate) => ({
          ...(currentGate.path === currentPath
            ? currentGate
            : createRouteGate(currentPath)),
          requiresActivityCompletion: true,
          hasCompletedActivity: true,
        }));
      },
    }),
    [currentPath],
  );

  const currentRouteGate =
    routeGate.path === currentPath ? routeGate : createRouteGate(currentPath);

  const disableNextForVideo =
    !hasFullFacilitatorProgramAccess &&
    currentRouteGate.requiresVideoCompletion &&
    !currentRouteGate.hasCompletedVideo &&
    !hasProgressPassedPath(currentPath, adminProgress);
  const disableNextForQuiz =
    !hasFullFacilitatorProgramAccess &&
    currentRouteGate.requiresQuizPass &&
    !currentRouteGate.hasPassedQuiz &&
    !hasProgressPassedPath(currentPath, adminProgress);
  const disableNextForActivity =
    !hasFullFacilitatorProgramAccess &&
    currentRouteGate.requiresActivityCompletion &&
    !currentRouteGate.hasCompletedActivity &&
    !hasProgressPassedPath(currentPath, adminProgress);
  const disableNext =
    (isCurrentPathInFlow &&
      (!hasLoadedAdminProgress || !hasSettledCurrentRouteGate)) ||
    disableNextForVideo ||
    disableNextForQuiz ||
    disableNextForActivity;

  const handleBack = () => {
    const currentIndex = trainingRoutes.findIndex(
      (route) => route.fullUrl === currentPath,
    );
    if (currentIndex > 0) {
      const previousRoute = trainingRoutes[currentIndex - 1];
      history.push(previousRoute.fullUrl);
    } else {
      history.push(path); // Redirect to the main path if no previous route
    }
  };

  const handleNext = () => {
    if (disableNext) {
      return;
    }

    if (currentPath === trainingRoutes[trainingRoutes.length - 1]?.fullUrl) {
      history.push(FACILITATOR_RESOURCE_LIBRARY_ROUTE);
      return;
    }

    const currentIndex = trainingRoutes.findIndex(
      (route) => route.fullUrl === currentPath,
    );
    if (currentIndex >= 0 && currentIndex < trainingRoutes.length - 1) {
      const nextRoute = trainingRoutes[currentIndex + 1];
      const progressAtCurrentPage = deriveProgressFromPath(
        currentPath,
        adminProgress,
      );
      const progressAtNextPage = deriveSequentialNextProgress(
        nextRoute.fullUrl,
        progressAtCurrentPage,
      );

      queryClient.setQueryData(ADMIN_PROGRESS_QUERY_KEY, progressAtNextPage);
      saveAdminProgressMutation.mutate(progressAtNextPage);
      history.push(nextRoute.fullUrl);
    }
  };

  return (
    <AppShell
      padding={0}
      styles={{
        body: {
          width: '100%',
        },
        main: {
          background:
            theme.colorScheme === 'dark'
              ? theme.colors.dark[8]
              : theme.colors.gray[0],
          width: '100%',
          maxWidth: '100%',
          overflowX: 'hidden',
          padding: 0,
          paddingBottom: '0px',
          paddingTop: 'var(--mantine-header-height, 0px)',
          paddingInlineStart: isMobileNav
            ? '0px'
            : 'var(--mantine-navbar-width, 0px)',
          paddingInlineEnd: '0px',
        },
      }}
      navbarOffsetBreakpoint={FACILITATOR_NAV_BREAKPOINT}
      asideOffsetBreakpoint={FACILITATOR_NAV_BREAKPOINT}
      navbar={
        <SideNav
          opened={opened}
          isMobileNav={isMobileNav}
          adminProgress={adminProgress}
          isLoadingAdminProgress={isLoadingAdminProgress}
        />
      }
      header={
        <FacilitatorHeader
          opened={opened}
          setOpened={setOpened}
          isMobileNav={isMobileNav}
        />
      }
    >
      <div
        className='flex flex-col h-full justify-between relative'
        style={{ fontSize: '1rem' }}
      >
        <VideoGateContext.Provider value={videoGateValue}>
          <div className='flex-1 h-full relative'>
            <SubrouteProgress />
            <Routes />
          </div>
        </VideoGateContext.Provider>
        {isCurrentPathInFlow && (
          <Footer
            routes={trainingRoutes}
            beginningOfProgram={
              currentPath === trainingRoutes[0]?.fullUrl
            }
            handleBack={handleBack}
            handleNext={handleNext}
            disableNext={disableNext}
          />
        )}
      </div>
    </AppShell>
  );
};

export default FascilitatorTraining;
